import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Divider,
  Tooltip,
  tooltipClasses,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Vaccines as VaccinesIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    //maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const VaccinationList = ({ records, onUpdate, showControls = true }) => {
  const [activeInputs, setActiveInputs] = useState({})

  const toggleInput = (id) => {
    setActiveInputs(prev => ({
      ...prev,
      [id]: !prev[id] // cambia el estado de ese item específico
    }));
  };

  const groupByName = (records) => {
    return records.reduce((acc, item) => {
      const vacuna = item.vaccine;
      if (!acc[vacuna]) {
        acc[vacuna] = [];
      }
      acc[vacuna].push(item);
      return acc;
    }, {});
  };
  const handleChange = (id, field, value) => {
    const index = records.findIndex((item) => item.id === id);
    if (index === -1) return; 

    const updated = [...records];
    updated[index][field] = value;
    updated[index]['new'] = true;

    onUpdate(updated);
  };

  return (
    <Box sx={{  margin: '0 auto' }}>
      <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', marginBottom: '2rem' }}>
        Historial de Vacunación
      </Typography>

      {/* Encabezado */}
      <Grid container spacing={2} sx={{ fontWeight: 'bold', px: 2, py: 1, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ccc' }}>
        <Grid item xs={2}>Vacuna</Grid>
        <Grid item xs={3}>Enfermedades que previene</Grid>
        <Grid item xs={7}>Dosis</Grid>
      </Grid>

      {/* Lista con scroll */}
      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        { Object.entries(groupByName(records)).map(([nombreVacuna, dosisArray], index) =>{ 
          return (
          <React.Fragment key={index}>
            <Grid container spacing={2} sx={{ px: 2, py: 1, alignItems: 'center' }}>
              <Grid item xs={2} sx={{ borderRadius: '5px' ,backgroundColor: dosisArray[0].color || '' }}>
                <Typography sx={{ fontSize: '0.90rem' }}>{nombreVacuna}</Typography>
              </Grid>
                <Grid item xs={3}>
                  <HtmlTooltip
                    title={
                      <React.Fragment>
                          <List dense sx={{ p: 0 }}>
                            {dosisArray[0].diseases.map((item, index) => (
                              <ListItem key={index} divider
                                    sx={{
                                      py: 0.5, // padding vertical reducido
                                      px: 1,   // padding horizontal reducido
                                      '&.MuiListItem-root': { minHeight: 'auto' }, // elimina altura mínima
                                    }}>
                                <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }}/>
                              </ListItem>
                            ))}
                          </List>
                      </React.Fragment>
                    }
                  >
                    <Typography sx={{
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '200px' // Ajustalo al tamaño que necesites
                    }}>• {dosisArray[0].diseases}</Typography>
                  </HtmlTooltip>
                </Grid>
                <Grid item xs={7}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  {
                    dosisArray.map((item)=>{
                      return (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Divider orientation="vertical" variant="middle" flexItem />
                            <HtmlTooltip
                              title={
                                <React.Fragment>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                    <Stack spacing={0.5}> {/* menor espacio vertical entre bloques */}
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <b>Dosis:</b>
                                        <Typography sx={{ fontSize: '0.75rem', px: 1 }}>{item.dose}</Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <b>Aplicado:</b>
                                        <Typography sx={{ fontSize: '0.75rem', px: 1 }}>{item.date_administration}</Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <b>Lote:</b>
                                        <Typography sx={{ fontSize: '0.75rem', px: 1 }}>{item.lot}</Typography>
                                      </Box>
                                    </Stack>
                                    {
                                      showControls ? (
                                        <IconButton color="primary" aria-label="Editar" onClick={() => toggleInput(item.id)}>
                                          <EditIcon sx={{ color: 'rgb(20, 143, 159)' }}/>
                                        </IconButton>
                                      ) : null
                                    }
                                  </Box>
                                </React.Fragment>
                              }
                            >
                              {
                                item.date_administration && item.lot ? (
                                  <IconButton color="success" aria-label="Show details">
                                    <VaccinesIcon />
                                  </IconButton>) : (
                                  <IconButton
                                    onClick={() => toggleInput(item.id)}
                                    color="success"
                                    aria-label="Show details"
                                    sx={{ color: 'grey.400' }}
                                  >
                                    <VaccinesIcon />
                                  </IconButton>
                                )
                              }
                            </HtmlTooltip>
                          </Box>
                          {
                            activeInputs[item.id] && showControls ? (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box>
                                  <TextField
                                    type="date"
                                    value={item.date_administration || ''}
                                    size='small'
                                    onChange={(e) =>
                                      handleChange(item.id, 'date_administration', e.target.value)
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                      sx={{
                                        '& input': {
                                          padding: '6px 8px', // reduce padding del input
                                          fontSize: '0.8rem'  // reduce tamaño de fuente
                                        },
                                        '& .MuiInputBase-root': {
                                          height: '32px', // puedes ajustar esto si quieres más compacto
                                        }
                                      }}
                                  />
                                  <TextField
                                    value={item.lot || ''}
                                    size='small'
                                    onChange={(e) =>
                                      handleChange(item.id, 'lot', e.target.value)
                                    }
                                    fullWidth
                                    placeholder='Lote de vacuna'
                                      sx={{
                                        '& input': {
                                          padding: '6px 8px', // reduce padding del input
                                          fontSize: '0.8rem'  // reduce tamaño de fuente
                                        },
                                        '& .MuiInputBase-root': {
                                          height: '32px', // puedes ajustar esto si quieres más compacto
                                        }
                                      }}
                                  />
                              </Box>
                              <IconButton color="error" aria-label="Cerrar" onClick={() => toggleInput(item.id)}>
                                <CancelIcon />
                              </IconButton>
                              </Box> 
                            ) : null
                          }
                        </Box>
                      )
                    })
                  } 
                  </Box>
                </Grid>
            </Grid>
            <Divider />
          </React.Fragment>
          )
         })
        }
      </Box>
    </Box>
  );
};

export default VaccinationList;
